import { t } from "@hakili/i18n";
import { Callout, Field, GuardedButton, Input } from "@hakili/ui";
import { CircleAlert, CircleCheck, Info } from "lucide-react";
import { useEffect, useState } from "react";

import { GUARD_CHIP } from "./d2-parts.js";
import { LOGIN_ACT, LOGIN_FORM, LOGIN_SUB, LOGIN_TITLE, LoginShell, QuietLink } from "./connexion-panel.js";

/**
 * WF-BNK-01, to the drawing (`hakili-bank.jsx`, `LoginScreen`) since round
 * `L-0`: the mark and the wordmark, the subtitle, the card with its two
 * fields and « Se connecter », « Mot de passe oublié » greyed as the drawing's
 * own stub (it draws the label and nothing behind it), the footer sentence.
 * The drawing's subtitle names « Banque X »; before the session the bank is
 * not known, so the build's subtitle names the space and the direction alone
 * (R81: nothing invented). The refusal renders where the drawing has none,
 * in the door's own words. The drawing's wordmark carries a spark over the
 * « ı »; `@hakili/ui`'s `HakiliMark` has no sparkle variant , said, and the
 * mark left as the kit lends it (the ruling of 15 September, row 6).
 */
/**
 * `FX-3`, `M3` (`S2-2`): **the second factor's field, which this screen did not have.**
 *
 * The sitting met « Un code de vérification est requis. » on a page with nowhere to type one, and read it as
 * « no two-step enrolment was ever offered ». Canon settles the obligation and it is NOT the defect:
 * `DECISION ADDED 06-02` , *« TOTP is optional for every role except `administration`, where it is
 * mandatory »* , and `06-23` says it again for this surface, *« TOTP optional for bank users »*. So no bank
 * seat is required to enrol; `domain/login.ts` demands a code « for every ENROLLED user, any role », and the
 * seat on staging was enrolled.
 *
 * The defect is therefore that an enrolled bank user could not log in AT ALL: the door asked for a code and
 * the screen had no field, which is `P0-UIK-021`'s failure , the console's own , repeated on another surface.
 * The field is revealed by the door's own `details.reason`, never shown by default: asking every banker for a
 * code most of them do not have is the other way to make a login unpassable.
 */
export function Connexion(props: {
  readonly refusal?: string | undefined;
  readonly pending: boolean;
  /**
   * The last refusal was the second factor's , `app-shell`'s `challenged()` state, which is `reasonOf(…) ===
   * CODE_DEMANDE` read once in the session rather than in every screen.
   */
  readonly codeRequired?: boolean | undefined;
  /**
   * `C-1` (BANK-V2, R-18): the session the banker was in has expired (`useSession`'s fourth state). The line says so in words
   * and the e-mail she was signed in with is kept in the field, so she retypes the password and nothing else.
   */
  readonly expired?: { readonly email: string } | null | undefined;
  readonly onSubmit: (credentials: { email: string; password: string; totpCode?: string }) => void;
  /**
   * `RESET-PASSWORD` R2 (frame 100): the quiet link under « Se connecter » that opens « Mot de passe oublié ». Absent, the link
   * is not drawn (a build whose door is shut has no link to a page that is not there).
   */
  readonly onForgot?: (() => void) | undefined;
  /** Frame 108: the password was just saved through a reset link; the sign-in says so, and every session was closed. */
  readonly resetDone?: boolean | undefined;
}): React.JSX.Element {
  const [email, setEmail] = useState(props.expired?.email ?? "");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const codeDemande = props.codeRequired === true;
  // A code request is the door asking for the second factor of a password that was RIGHT: not a refusal, so no field is
  // invalid and the typed password stays (the banker adds the code and presses the same button).
  const refused = props.refusal !== undefined && !codeDemande;

  // The e-mail an expiry kept is put in the field when the expiry arrives (the screen may already be mounted).
  const expiredEmail = props.expired?.email;
  useEffect(() => {
    if (expiredEmail !== undefined) setEmail(expiredEmail);
  }, [expiredEmail]);
  // A refusal clears the password: it is retyped, never resubmitted as it was. A code request does not.
  useEffect(() => {
    if (props.refusal !== undefined && props.codeRequired !== true) setPassword("");
  }, [props.refusal, props.codeRequired]);

  const ready = email.trim() !== "" && password !== "";

  // Typed as what it USES rather than as React's `FormEvent`, which React 19
  // deprecates as a name for an event that does not exist.
  const submit = (event: Pick<Event, "preventDefault">): void => {
    event.preventDefault();
    if (!ready || props.pending) return;
    props.onSubmit({ email, password, ...(code === "" ? {} : { totpCode: code }) });
  };

  return (
    <LoginShell reading={props.pending}>
      <h1 className={LOGIN_TITLE} data-testid="bank-login-titre">
        {t("bank.login.titreConnexion")}
      </h1>
      <p className={LOGIN_SUB} data-testid="bank-login-sous-titre">
        {t("bank.login.sousTitre")}
      </p>
      <form className={LOGIN_FORM} onSubmit={submit} data-testid="bank-login" noValidate>
        {props.expired === null || props.expired === undefined ? null : (
          <Callout
            tone="info"
            icon={<Info className="h-4 w-4" aria-hidden="true" />}
            testId="bank-login-expiree"
          >
            <strong>{t("bank.login.expiree")}</strong>
          </Callout>
        )}
        {props.resetDone === true ? (
          <Callout
            tone="ok"
            icon={<CircleCheck className="h-4 w-4" aria-hidden="true" />}
            testId="bank-login-reinit-faite"
          >
            <p>
              <strong>{t("bank.login.reinitFaite")}</strong>
            </p>
            <p>{t("bank.login.reinitFaiteSuite")}</p>
          </Callout>
        ) : null}
        {props.refusal === undefined ? null : codeDemande ? (
          <Callout
            tone="info"
            icon={<Info className="h-4 w-4" aria-hidden="true" />}
            testId="bank-login-refus"
          >
            {props.refusal}
          </Callout>
        ) : (
          <Callout
            tone="bad"
            icon={<CircleAlert className="h-4 w-4" aria-hidden="true" />}
            testId="bank-login-refus"
          >
            {props.refusal}
          </Callout>
        )}
        <Field label={t("bank.login.email")} htmlFor="bank-login-email">
          <Input
            id="bank-login-email"
            type="email"
            value={email}
            onChange={setEmail}
            autoComplete="username"
            invalid={refused}
            required
            testId="bank-login-email"
          />
        </Field>
        <Field label={t("bank.login.motDePasse")} htmlFor="bank-login-password">
          <Input
            id="bank-login-password"
            type="password"
            value={password}
            onChange={setPassword}
            autoComplete="current-password"
            invalid={refused}
            required
            testId="bank-login-password"
          />
        </Field>
        {codeDemande ? (
          <Field label={t("bank.login.code")} htmlFor="bank-login-code" hint={t("bank.login.codeAide")}>
            <Input
              id="bank-login-code"
              value={code}
              onChange={setCode}
              inputMode="numeric"
              // The handset's own offer of the authenticator's code, the console's same hint.
              autoComplete="one-time-code"
              testId="bank-login-code"
            />
          </Field>
        ) : null}
        <div className={LOGIN_ACT}>
          {props.pending ? (
            <GuardedButton
              disabled
              type="submit"
              size="lg"
              reason={t("bank.login.encours")}
              reasonTone="neutral"
              testId="bank-login-submit"
            >
              {t("bank.login.entrer")}
            </GuardedButton>
          ) : ready ? (
            <GuardedButton type="submit" variant="primary" size="lg" testId="bank-login-submit">
              {t("bank.login.entrer")}
            </GuardedButton>
          ) : (
            <GuardedButton
              disabled
              type="submit"
              size="lg"
              reason={t("bank.login.saisirIdentifiants")}
              {...GUARD_CHIP}
              testId="bank-login-submit"
            >
              {t("bank.login.entrer")}
            </GuardedButton>
          )}
        </div>
        {props.onForgot === undefined ? null : (
          <QuietLink onClick={props.onForgot} testId="bank-login-oublie">
            {t("bank.login.oublie")}
          </QuietLink>
        )}
      </form>
    </LoginShell>
  );
}
